import { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useBands } from '@/hooks/useBands'
import type { Band } from '@/types'

interface BandContextValue {
  bands: Band[]
  selectedBandId: string
  setSelectedBandId: (id: string) => void
  refetchBands: () => Promise<void>
  createBand: (name: string) => Promise<Band>
  updateBand: (id: string, name: string) => Promise<void>
  deleteBand: (id: string) => Promise<void>
}

const BandContext = createContext<BandContextValue | null>(null)

const STORAGE_KEY = 'band-manager-selected-band'

export const BandProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth()
  const { bands, refetch: refetchBands, createBand, updateBand, deleteBand } = useBands(user?.id)
  const [selectedBandId, setSelectedBandIdState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) ?? ''
  })

  const setSelectedBandId = (id: string) => {
    setSelectedBandIdState(id)
    localStorage.setItem(STORAGE_KEY, id)
  }

  // Auto-select if only one band, or clear if stored band no longer exists
  useEffect(() => {
    if (bands.length === 0) return
    if (!selectedBandId && bands.length === 1) {
      setSelectedBandId(bands[0].id)
    } else if (selectedBandId && !bands.find((b) => b.id === selectedBandId)) {
      setSelectedBandId(bands.length === 1 ? bands[0].id : '')
    }
  }, [bands, selectedBandId])

  return (
    <BandContext.Provider value={{ bands, selectedBandId, setSelectedBandId, refetchBands, createBand, updateBand, deleteBand }}>
      {children}
    </BandContext.Provider>
  )
}

export const useBandContext = () => {
  const ctx = useContext(BandContext)
  if (!ctx) throw new Error('useBandContext must be used within BandProvider')
  return ctx
}
